import {
    Alert,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { addDays, previousMonday } from "date-fns";
import { times } from "lodash";
import { useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import useCurrentUser from "~/modules/auth/hooks/useCurrentUser";
import useFieldworkerUpdate from "~/modules/user/api/useFieldworkerUpdate";

import useFieldworkerGetAvailabilityConfig from "../../api/useFieldworkerGetAvailabilityConfig";
import FieldworkerAvailabilitySlots from "./FieldworkerAvailabilitySlots";

const formatter = new Intl.DateTimeFormat("nl-NL", { weekday: "short" });

export type FieldworkerAvailabilityFormData = {
    maxShiftsPerWeek: number;
    noCoolOffBetweenShifts: boolean;
    days: { [slotId: string]: boolean[] };
};

export default function FieldworkerAvailabilityForm() {
    const { t } = useTranslation("fieldworker");
    const user = useCurrentUser();

    if (user?.fieldworker === null || user?.fieldworker === undefined) {
        throw new Error("Need user to be available");
    }

    const { fieldworkerUpdate, loading, error } = useFieldworkerUpdate(user.id);
    const { data: availabilityConfig } = useFieldworkerGetAvailabilityConfig();
    const navigate = useNavigate();

    const defaultAvailability: { [slotId: string]: boolean[] } = {};
    availabilityConfig?.forEach(({ availabilities }) => {
        availabilities?.forEach(slot => {
            defaultAvailability[slot.id] = user.fieldworker?.availability?.[slot.id] || Array(7).fill(true);
        });
    });

    const {
        control,
        handleSubmit,
        watch,
        formState: { isDirty },
    } = useForm<FieldworkerAvailabilityFormData>({
        defaultValues: {
            noCoolOffBetweenShifts: user.fieldworker.coolOffBetweenShiftsInHours < 1,
            maxShiftsPerWeek: user.fieldworker.maxShiftsPerWeek ?? 10,
            days: user?.fieldworker.availability,
        },
    });

    const userAvailabilityDays = watch("days");

    const referenceDay = previousMonday(new Date());

    return (
        <form
            onSubmit={handleSubmit(async data => {
                await fieldworkerUpdate({
                    availability: data.days,
                    maxShiftsPerWeek: data.maxShiftsPerWeek,
                    coolOffBetweenShifts: !data.noCoolOffBetweenShifts,
                    hasConfirmedAvailability: true,
                });

                navigate("/");
            })}
        >
            {error && (
                <Alert severity="error">
                    <Typography>{error.message}</Typography>
                </Alert>
            )}
            <Stack gap={2} marginTop={4}>
                <SelectElement
                    fullWidth
                    control={control}
                    label={t("fieldworkerAvailabilityForm.labelMaxShiftsPerWeek")}
                    name="maxShiftsPerWeek"
                    options={times(9, number => ({
                        id: number + 2,
                        label: number + 2,
                    }))}
                />

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                {times(7, number => (
                                    <TableCell key={number}>
                                        {formatter.format(addDays(referenceDay, number))}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {availabilityConfig?.map(({ id, name, availabilities, period }) => (
                                <FieldworkerAvailabilitySlots
                                    key={id}
                                    name={name}
                                    period={period}
                                    availabilities={availabilities ?? []}
                                    control={control}
                                    userAvailabilityDays={userAvailabilityDays}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <SwitchElement
                    control={control}
                    name="noCoolOffBetweenShifts"
                    label={t("fieldworkerAvailabilityForm.noCoolOffBetweenShifts")}
                />

                <Typography variant="body2">{t("fieldworkerAvailabilityForm.notes")}</Typography>

                <Stack direction="row" justifyContent="end">
                    <Button variant="contained" size="large" type="submit" loading={loading} disabled={!isDirty}>
                        {t("fieldworkerAvailabilityForm.btnSave")}
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
}
